import {
	PortalChangePasswordModal,
	PortalConfirmDialog,
	PortalInviteModal,
	PortalUserDrawer,
	PortalUsersHeader,
	PortalUsersListLoading,
	PortalUsersPagination,
	PortalUsersStats,
	PortalUsersTable,
	PortalUsersToolbar,
	resolveErrorMessage,
} from "@seller-hermes/features/portal/components/portal-users";
import { getValidActiveSellerGroupId } from "@seller-hermes/features/portal/portal-local-context";
import { changePasswordContext } from "@seller-hermes/features/portal/portal-user-types";
import {
	PortalUsersService,
	toManagedUser,
} from "@seller-hermes/features/portal/portal-users.api";
import { PortalManagedUser } from "@seller-hermes/features/portal/portal-users.types";
import { PortalService } from "@seller-hermes/features/portal/portal.api";
import { PortalProfile } from "@seller-hermes/features/portal/portal.types";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import "./portal-users.css";


const STALE_TIME = 60_000;
const PAGE_SIZE = 20;
const PROFILE_ORDER: PortalProfile[] = ["LOG", "GO", "PAY"];
const ALL_PORTAL_OPERATIONS = "ALL";
type OperationFilter = PortalProfile | typeof ALL_PORTAL_OPERATIONS | "";

export const PortalUsersPage = () => {
	const queryClient = useQueryClient();

	const [activeSellerGroupId, setActiveSellerGroupId] = useState<
		string | null
	>(null);
	const [activeProfile, setActiveProfile] = useState<OperationFilter>("");
	const [search, setSearch] = useState("");
	const [typeFilter, setTypeFilter] = useState("");
	const [page, setPage] = useState(0);
	const [selectedUser, setSelectedUser] = useState<PortalManagedUser | null>(
		null,
	);
	const [selectedMenu, setSelectedMenu] = useState<number | null>(null);
	const [isInviteOpen, setIsInviteOpen] = useState(false);
	const [confirmDeactivate, setConfirmDeactivate] =
		useState<PortalManagedUser | null>(null);
	const [passwordTarget, setPasswordTarget] =
		useState<PortalManagedUser | null>(null);
	const [busyUserId, setBusyUserId] = useState<string | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);

	const sellerGroupsQuery = useQuery(
		["account-seller-groups"],
		() => PortalUsersService.listAccountSellerGroups(),
		{ staleTime: STALE_TIME, retry: false },
	);

	const sellerGroups = useMemo(
		() => sellerGroupsQuery.data ?? [],
		[sellerGroupsQuery.data],
	);

	// The user's own seller-group contexts (the operations they actually hold an
	// active link to). These are the groups that can be sent as the active
	// context header without a 403, so one of them is always the request header.
	// Homologation/unavailable placeholders are excluded.
	const meQuery = useQuery(["portal-me"], () => PortalService.getMe(), {
		staleTime: STALE_TIME,
		retry: false,
	});

	const myContexts = useMemo(() => {
		const available = (meQuery.data?.seller_groups ?? []).filter(
			(context) => context.status === "available",
		);
		const production = available.filter(
			(context) => !context.is_homologation,
		);
		return production.length > 0 ? production : available;
	}, [meQuery.data]);

	// Default the active context (header) and the operation filter once contexts
	// load. Only fills in unset values, never overrides a user selection — the
	// operation is an independent filter, decoupled from the header group.
	// Defaults the filter to "all operations" when the account exposes more than
	// one product; otherwise to the single product. Waits for the administered
	// groups to settle so multi-op accounts are detected reliably.
	useEffect(() => {
		if (myContexts.length === 0) return;
		const fromContext = getValidActiveSellerGroupId();
		const match =
			myContexts.find((context) => context.id === fromContext) ??
			myContexts[0];
		if (!activeSellerGroupId) setActiveSellerGroupId(match.id);

		if (!activeProfile && !sellerGroupsQuery.isLoading) {
			const available = new Set<string>([
				...sellerGroups.map((group) => group.profile ?? ""),
				...myContexts.map((context) => context.profile),
			]);
			const opCount = PROFILE_ORDER.filter((profile) =>
				available.has(profile),
			).length;
			setActiveProfile(
				opCount > 1 ? ALL_PORTAL_OPERATIONS : match.profile,
			);
		}
	}, [
		myContexts,
		activeSellerGroupId,
		activeProfile,
		sellerGroups,
		sellerGroupsQuery.isLoading,
	]);

	// Reset to the first page whenever the result set changes.
	useEffect(() => {
		setPage(0);
	}, [activeSellerGroupId, activeProfile, search, typeFilter]);

	// LOG/GO scope to a single product; the "all operations" sentinel widens the
	// request to every product in the account. Empty only before a profile is
	// resolved.
	const operationParam = activeProfile || undefined;

	const usersQuery = useQuery(
		[
			"portal-users",
			activeSellerGroupId,
			operationParam ?? "",
			search,
			typeFilter,
			page,
		],
		() =>
			PortalUsersService.listUsers(
				{
					search: search.trim() || undefined,
					type: typeFilter || undefined,
					operation: operationParam,
					from: page * PAGE_SIZE,
					size: PAGE_SIZE,
				},
				activeSellerGroupId ?? undefined,
			),
		{
			enabled: Boolean(activeSellerGroupId) && Boolean(activeProfile),
			keepPreviousData: true,
			staleTime: STALE_TIME,
			retry: false,
		},
	);

	const total = usersQuery.data?.total ?? 0;
	const activeCount = usersQuery.data?.active_count ?? 0;
	const inactiveCount = usersQuery.data?.inactive_count ?? 0;

	const isInitialUsersLoad =
		Boolean(activeSellerGroupId) && usersQuery.isLoading;
	const isListRefreshing = usersQuery.isFetching && !usersQuery.isLoading;

	const users = useMemo(
		() => (usersQuery.data?.data ?? []).map(toManagedUser),
		[usersQuery.data],
	);

	const operations = useMemo(
		() => Array.from(new Set(users.flatMap((user) => user.operations))),
		[users],
	);

	// Operations available to filter by are the products the user can manage:
	// every administered seller group's product (account-wide for a global
	// MASTER), plus the user's own contexts as a fallback.
	const operationOptions = useMemo(() => {
		const available = new Set<string>([
			...sellerGroups.map((group) => group.profile ?? ""),
			...myContexts.map((context) => context.profile),
		]);
		return PROFILE_ORDER.filter((profile) => available.has(profile));
	}, [sellerGroups, myContexts]);

	const showAllOperations = operationOptions.length > 1;
	const activeOperationFilter =
		activeProfile === ALL_PORTAL_OPERATIONS ? "" : activeProfile;

	const handleOperationChange = (operation: string) => {
		setSelectedUser(null);
		if (operation === ALL_PORTAL_OPERATIONS) {
			setActiveProfile(ALL_PORTAL_OPERATIONS);
			return;
		}
		setActiveProfile(operation as PortalProfile);
		// When the user personally holds a context for this product, send it as the
		// active header too, so per-group (non-MASTER) scoping follows the choice.
		const match = myContexts.find(
			(context) => context.profile === operation,
		);
		if (match) setActiveSellerGroupId(match.id);
	};

	const invalidateUsers = () =>
		queryClient.invalidateQueries(["portal-users"]);

	const setActiveMutation = useMutation(
		({ userId, active }: { userId: string; active: boolean }) =>
			PortalUsersService.setUserActive(
				userId,
				active,
				activeSellerGroupId ?? undefined,
			),
		{
			onMutate: ({ userId }) => setBusyUserId(userId),
			onError: (error: unknown) =>
				setActionError(resolveErrorMessage(error)),
			onSettled: async () => {
				setBusyUserId(null);
				await invalidateUsers();
			},
		},
	);

	const openDetail = (user: PortalManagedUser) => {
		setSelectedMenu(null);
		setSelectedUser(user);
	};

	// Keep the open drawer in sync with refreshed data.
	const drawerUser = selectedUser
		? (users.find((user) => user.id === selectedUser.id) ?? selectedUser)
		: null;

	const handleUserStatusChange = (userId: string, active: boolean) => {
		setSelectedUser((prev) =>
			prev?.id === userId
				? { ...prev, status: active ? "Ativo" : "Inativo" }
				: prev,
		);
	};

	const handleLinkRemoved = (userId: string, sgId: string) => {
		setSelectedUser((prev) => {
			if (prev?.id !== userId) return prev;
			const links = prev.links.filter((link) => link.id !== sgId);
			// No links left means the user is no longer manageable here; close.
			if (links.length === 0) return null;
			return { ...prev, links };
		});
	};

	// Tipo do usuário logado em cada operação — define quais tipos ele pode
	// conceder ali. O /portal/me já devolve isso por seller group; usar o
	// user.type global permitiria conceder MASTER numa operação onde ele é ADMIN.
	const sellerGroupTypes = useMemo(
		() =>
			Object.fromEntries(
				(meQuery.data?.seller_groups ?? []).map((sellerGroup) => [
					sellerGroup.id,
					sellerGroup.type,
				]),
			),
		[meQuery.data],
	);

	// Operação compartilhada que habilita alterar a senha do alvo (null = botão
	// escondido). A decisão real é do backend; aqui só escondemos o inviável.
	const passwordContextFor = (user: PortalManagedUser) =>
		changePasswordContext(sellerGroupTypes, user, meQuery.data?.user?.id);

	const isLoading =
		sellerGroupsQuery.isLoading || meQuery.isLoading || isInitialUsersLoad;

	// As operações administradas só alimentam convite, filtro e vínculos do
	// drawer. Se falharem, a lista (que vem de outra rota) e a troca de senha
	// continuam: antes a tela inteira virava uma mensagem de erro (LOG-710).
	const sellerGroupsFailed = sellerGroupsQuery.isError;

	return (
		<main
			className="min-h-screen bg-ab-bg font-sans text-ab-base text-ab-fg"
			onClick={() => setSelectedMenu(null)}
		>
			<div
				className="mx-auto max-w-5xl px-ab-6 py-ab-8"
				onClick={(event) => event.stopPropagation()}
			>
				<PortalUsersHeader accountName={meQuery.data?.account?.name} />
				<PortalUsersStats
					total={total}
					activeCount={activeCount}
					inactiveCount={inactiveCount}
					operations={operations}
					isLoading={isListRefreshing}
				/>
				<div className="rounded-ab border border-ab-border bg-ab-card">
					<PortalUsersToolbar
						search={search}
						onSearchChange={setSearch}
						operations={operationOptions}
						showAllOperations={showAllOperations}
						activeOperation={activeProfile}
						onOperationChange={handleOperationChange}
						typeFilter={typeFilter}
						onTypeFilterChange={setTypeFilter}
						onInvite={() => setIsInviteOpen(true)}
						canInvite={!sellerGroupsFailed}
					/>

					{actionError ? (
						<p className="px-ab-4 py-ab-2 text-ab-xs text-ab-destructive">
							{actionError}
						</p>
					) : null}

					{sellerGroupsFailed && !isLoading ? (
						<p
							role="status"
							className="px-ab-4 py-ab-2 text-ab-xs text-ab-fg-muted"
						>
							Não foi possível carregar suas operações. Convidar usuários e
							editar vínculos ficam indisponíveis por enquanto.
						</p>
					) : null}

					{isLoading ? (
						<PortalUsersMessage text="Carregando usuários…" />
					) : !sellerGroupsFailed && sellerGroups.length === 0 ? (
						<PortalUsersMessage text="Você não administra nenhuma operação." />
					) : usersQuery.isError ? (
						<PortalUsersMessage text="Não foi possível carregar os usuários desta operação." />
					) : users.length === 0 ? (
						<PortalUsersMessage text="Nenhum usuário encontrado." />
					) : (
						<div
							className={`relative${isListRefreshing ? " opacity-60 pointer-events-none" : ""}`}
							data-ui="users-table-scroll"
						>
							{isListRefreshing ? (
								<PortalUsersListLoading />
							) : null}
							<PortalUsersTable
								users={users}
								activeOperation={activeOperationFilter}
								selectedMenu={selectedMenu}
								busyUserId={busyUserId}
								onToggleMenu={(index) =>
									setSelectedMenu((current) =>
										current === index ? null : index,
									)
								}
								onOpenDetail={openDetail}
								canChangePassword={(user) =>
									passwordContextFor(user) !== null
								}
								onChangePassword={(user) => {
									setSelectedMenu(null);
									setPasswordTarget(user);
								}}
								onDeactivate={(user) =>
									setConfirmDeactivate(user)
								}
								onReactivate={(user) =>
									setActiveMutation.mutate({
										userId: user.id,
										active: true,
									})
								}
							/>
							{total > PAGE_SIZE ? (
								<PortalUsersPagination
									page={page}
									pageSize={PAGE_SIZE}
									total={total}
									isLoading={isListRefreshing}
									onPageChange={setPage}
								/>
							) : null}
						</div>
					)}
				</div>
			</div>

			<PortalInviteModal
				isOpen={isInviteOpen}
				onClose={() => setIsInviteOpen(false)}
				sellerGroups={sellerGroups}
				activeSellerGroupId={activeSellerGroupId}
				sellerGroupTypes={sellerGroupTypes}
			/>
			<PortalConfirmDialog
				isOpen={confirmDeactivate !== null}
				title="Desativar usuário?"
				description={`${confirmDeactivate?.email ?? "Este usuário"} perderá acesso ao sistema. Esta ação pode ser desfeita reativando o usuário.`}
				confirmLabel="Desativar"
				isLoading={setActiveMutation.isLoading}
				onCancel={() => setConfirmDeactivate(null)}
				onConfirm={() => {
					if (confirmDeactivate) {
						setActiveMutation.mutate(
							{ userId: confirmDeactivate.id, active: false },
							{ onSettled: () => setConfirmDeactivate(null) },
						);
					}
				}}
			/>
			<PortalUserDrawer
				user={drawerUser}
				accountName={meQuery.data?.account?.name}
				sellerGroups={sellerGroups}
				activeSellerGroupId={activeSellerGroupId}
				sellerGroupTypes={sellerGroupTypes}
				canChangePassword={Boolean(
					drawerUser && passwordContextFor(drawerUser) !== null,
				)}
				onChangePassword={setPasswordTarget}
				onClose={() => setSelectedUser(null)}
				onUserStatusChange={handleUserStatusChange}
				onLinkRemoved={handleLinkRemoved}
			/>
			{passwordTarget ? (
				<PortalChangePasswordModal
					user={passwordTarget}
					sellerGroupId={passwordContextFor(passwordTarget) ?? undefined}
					onClose={() => setPasswordTarget(null)}
				/>
			) : null}
		</main>
	);
};

const PortalUsersMessage = ({ text }: { text: string }) => (
	<div className="py-ab-8 px-ab-4 text-center text-ab-sm text-ab-fg-muted">
		{text}
	</div>
);
